import { resolveFormTenantId } from "../../utils/tenantContext";
// Unified date logic applied
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import { handbookAcknowledgementSchema } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";


const HandbookAcknowledgement = ({ positionName, onClose }) => {
  const [handbookId, setHandbookId] = useState(null);


  const methods = useForm({
    resolver: zodResolver(handbookAcknowledgementSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeContractorName: "",
      applicantSignature: "",
      applicantSignedAt: "",
      positionAppliedFor: positionName || "",
      applicantStatementAccepted: false,
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: ""
    },
    mode: "onChange"
  });

  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      if (handbookId) {
        payload.handbookId = handbookId;
      }

      let response;
      if (handbookId) {
        response = await request(`/handbooks/${handbookId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/handbooks', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Handbook Acknowledgement processed successfully!");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,

    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeContractorName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const { handleSubmit, setValue, watch, reset } = methods;

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  // Load Data Effect
  // Load Data Effect
  useEffect(() => {
    const fetchHandbookAcknowledgement = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !handbookId) return;

      try {
        if (!actualFromId && !targetUserId && !handbookId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeContractorName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              applicantSignedAt: "",
              positionAppliedFor: positionName || "",
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/handbooks/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/handbooks?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/handbooks/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) {
              data = response.result[0];
            }
          } else if (response.result?.handbook) {
            data = response.result.handbook;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId, 10));
            }
          }
        }

        if (data) {
          setHandbookId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeContractorName: data.employeeContractorName || data.applicantName || "",
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
          });
        }
      } catch (error) {
        console.error("Error fetching handbook acknowledgement:", error);
      }
    };

    fetchHandbookAcknowledgement();
  }, [targetUserId, reset, currentUserData, isAdmin, handbookId, actualFromId, positionName]);






  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Handbook Acknowledgement" />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-6">
                <div className="mb-6 space-y-4 text-sm leading-relaxed text-gray-800">
                  <p>
                    This is to acknowledge that I have received a copy of the
                    Agency's Handbook and understand that it contains important
                    information on general personnel policies and on my privileges
                    and obligations as an employee. I agree that I will read and
                    comply with the materials in the Handbook. If I do not
                    understand any of these provisions, I agree to contact the
                    Agency for clarification.
                  </p>
                  <p>
                    The Agency may change, supplement or rescind any policies,
                    benefits, or practices described in the Handbook from time to
                    time at its sole and absolute discretion, with or without prior
                    notice with the exception of the employment at-will provisions.
                  </p>
                  <p>
                    No statement(s) in the Handbook or in other statement(s) of the
                    Company policies are to be construed either as an expressed or
                    implied promise of continuing employment, unless expressly
                    agreed and confirmed in writing by both the Company and the
                    Employee.
                  </p>
                  <p>
                    Other than the Administrator of the Agency, no manager,
                    supervisor or representative has authority to enter into any
                    agreement, expressed or implied, for employment for any period
                    of time, or to make any agreement for employment other than
                    at-will. Only the Administrator has the authority to make any
                    such agreement and then only in writing and signed by the
                    Administrator.
                  </p>
                  <p>
                    Further, I understand that employment with the Agency is at the
                    mutual consent of the employee and the Agency. Accordingly,
                    either the employee or the Agency can terminate the employment
                    relationship at-will, with or without cause, at any time.
                  </p>
                </div>
              </fieldset>
              <ApplicationFormFooter
                page={1}
                setPage={() => { }}
                totalPages={1}
                methods={methods}
                onSave={handleSave}
                onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                onStatusUpdate={handleStatusAction}
                isSignatureModalOpen={isSignatureModalOpen}
                openSignatureModal={openSignatureModal}
                closeSignatureModal={closeSignatureModal}
                handleSignatureApply={handleSignatureApply}
                signerName={signerName}
                showCertification={true}
                requireSignatures={true}
                showSignatureSections={true}
                applicantName={watch("employeeContractorName") || ""}
                applicantSignature={watch("applicantSignature") || ""}
                applicantSignedAt={watch("applicantSignedAt") || ""}
                reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                reviewedBySignature={watch("reviewedBySignature") || ""}
                reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                submitLabel="Submit & Print"
                isReadOnly={isReadOnly}
                onClose={closeForm}
                currentStatus={currentStatus}
              />
            </form>


          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default HandbookAcknowledgement;
